import React, { useEffect, useState, useMemo } from "react";
import { client } from "@/api/client.gen";
import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";

const charCount = (obj: any) => JSON.stringify(obj).length;

const CollapsibleSection = ({ title, badge, children }: { title: string; badge: string; children: React.ReactNode }) => {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button onClick={() => setOpen(!open)} className="hsub flex items-center gap-1 cursor-pointer select-none">
                <span className="fold-chevron text-xs">{open ? "▼" : "▶"}</span>
                {title}
                <span className="hint">{badge}</span>
            </button>
            {open && children}
        </div>
    );
};

const ClientViewChat = observer(({ tagIdsPart }: { tagIdsPart: string }) => {
    const tagIds = useMemo(
        () => tagIdsPart ? tagIdsPart.split("-").map(id => parseInt(id)).filter(id => !isNaN(id)) : [],
        [tagIdsPart]
    );

    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);

        client.post({
            url: "/api/chat/get-client-view-chat",
            body: { tag_ids: tagIds },
            headers: { "Content-Type": "application/json" },
        }).then((response: any) => {
            if (response.data) {
                setData(response.data);
            } else {
                setError(response.error?.detail || appState.t("chat.failedToLoad"));
            }
        }).catch((err: any) => {
            setError(err.message || appState.t("chat.requestFailed"));
        }).finally(() => {
            setLoading(false);
        });
    }, [tagIdsPart]);

    if (loading) return <div className="empty-state">{appState.t("common.loading")}</div>;
    if (error) return <div className="error-box">{error}</div>;
    if (!data || data.message !== "success") return <div className="empty-state">{appState.t("chat.noConversation")}</div>;

    return (
        <div className="p-2 mt-3 max-w-4xl mx-auto gap-1">
            <div className="hint">
                Model: {data.model_key} | Conversation ID: {data.conversation_id}
            </div>

            {data.system_messages?.length > 0 && (
                <CollapsibleSection title={appState.t("chat.systemMessages")} badge={`${charCount(data.system_messages).toLocaleString()} chars`}>
                    {data.system_messages.map((msg: any, i: number) => (
                        <pre key={i} className="code-block">
                            {JSON.stringify(msg, null, 2)}
                        </pre>
                    ))}
                </CollapsibleSection>
            )}
            {data.tools?.length > 0 && (
                <CollapsibleSection title={`Tools (${data.tools.length})`} badge={`${charCount(data.tools).toLocaleString()} chars`}>
                    {data.tools.map((tool: any, i: number) => (
                        <pre key={i} className="code-block">
                            {JSON.stringify(tool, null, 2)}
                        </pre>
                    ))}
                </CollapsibleSection>
            )}
            <CollapsibleSection title={appState.t("chat.messages")} badge={`${charCount(data.messages ?? []).toLocaleString()} chars`}>
                {data.messages?.map((msg: any, i: number) => (
                    <div key={i} className={`p-1 ${msg.role === "assistant" ? "panel-elevated" : ""}`}>
                        <div className="label-xs">{msg.role}</div>
                        <pre className="code-block">
                            {JSON.stringify(msg, null, 2)}
                        </pre>
                    </div>
                ))}
            </CollapsibleSection>

            
        </div>
    );
});

export default ClientViewChat;
